/**
 * BillingProfile - Form for billing/invoicing information
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { BillingInfo } from '../types';
import { API_URL } from '../config';

interface BillingProfileProps {
  onBack: () => void;
  onShowContact?: () => void;
}

const BillingProfile: React.FC<BillingProfileProps> = ({ onBack, onShowContact }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    company_name: '',
    vat_number: '',
    fiscal_code: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Italy',
    sdi_code: '',
    pec: ''
  });

  useEffect(() => {
    loadBillingInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBillingInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/billing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setBillingInfo({
            company_name: data.company_name || '',
            vat_number: data.vat_number || '',
            fiscal_code: data.fiscal_code || '',
            address: data.address || '',
            city: data.city || '',
            postal_code: data.postal_code || '',
            country: data.country || 'Italy',
            sdi_code: data.sdi_code || '',
            pec: data.pec || ''
          });
        }
      }
    } catch (err) {
      console.error('Error loading billing info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof BillingInfo, value: string) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${API_URL}/api/billing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billingInfo)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error saving data');
      }

      setSuccess('Billing information saved successfully!');
    } catch (err: any) {
      setError(err.message || 'Error saving data');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)' }}>
      <AppBar position="static" sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <Toolbar>
          <IconButton onClick={onBack} sx={{ color: 'white', mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'white' }}>
            Billing Information
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={8} sx={{ p: 4, borderRadius: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#003399', mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399' }}>
                Billing Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your billing details for invoicing
              </Typography>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

          <Grid container spacing={3}>
            {/* Company/Personal Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
                Invoice Recipient
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name / Full Name"
                value={billingInfo.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="e.g. John Smith Ltd"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT Number"
                value={billingInfo.vat_number}
                onChange={(e) => handleChange('vat_number', e.target.value)}
                placeholder="e.g. GB123456789"
                helperText="Your VAT registration number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax ID / Fiscal Code"
                value={billingInfo.fiscal_code}
                onChange={(e) => handleChange('fiscal_code', e.target.value.toUpperCase())}
                placeholder="e.g. ABCDEF12G34H567I"
                helperText="Tax identification number"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003399', mb: 2, mt: 2 }}>
                Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={billingInfo.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={billingInfo.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="London"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Postal Code"
                value={billingInfo.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="SW1A 1AA"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Country"
                value={billingInfo.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003399', mb: 2, mt: 2 }}>
                Electronic Invoicing (required for Italy)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SDI Code"
                value={billingInfo.sdi_code}
                onChange={(e) => handleChange('sdi_code', e.target.value.toUpperCase())}
                placeholder="0000000"
                helperText="7 characters - Italian SDI recipient code"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PEC Email"
                value={billingInfo.pec}
                onChange={(e) => handleChange('pec', e.target.value.toLowerCase())}
                placeholder="invoices@pec.company.com"
                helperText="Certified email (alternative to SDI)"
              />
            </Grid>

            {/* Info Box */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> For Italian electronic invoicing, please provide either the SDI Code 
                  or a PEC email address. If you don't have an SDI code, enter "0000000" and provide your PEC.
                </Typography>
              </Alert>
            </Grid>
          </Grid>

          {/* Save Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                px: 4,
                py: 1.5,
                bgcolor: '#003399',
                '&:hover': { bgcolor: '#002266' }
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>

          {/* Contact Link */}
          {onShowContact && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={onShowContact}
                sx={{ color: '#666' }}
              >
                Need help? Contact Us
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default BillingProfile;
